import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

/** Coerces API numbers (often strings like "45.00") to a finite number. */
export function toNumber(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const n = parseFloat(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
}

const moneyFormatters = new Map();

function moneyFormatter(currency, digits) {
  const key = `${currency}|${digits}`;
  if (!moneyFormatters.has(key)) {
    let formatter = null;
    try {
      formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });
    } catch {
      // Unknown currency code; fall back to "CODE 12.00" below.
    }
    moneyFormatters.set(key, formatter);
  }
  return moneyFormatters.get(key);
}

/**
 * formatMoney(45, 'USD') → "$45.00"
 * formatMoney(45, 'USD', { trim: true }) → "$45" (drops cents on whole amounts)
 */
export function formatMoney(value, currency = 'USD', { trim = false } = {}) {
  const amount = toNumber(value);
  const code = String(currency || 'USD').toUpperCase();
  const digits = trim && Number.isInteger(amount) ? 0 : 2;
  const formatter = moneyFormatter(code, digits);
  return formatter ? formatter.format(amount) : `${code} ${amount.toFixed(digits)}`;
}

/** Parses "HH:mm" / "H:mm:ss" (as the API returns) into minutes after midnight. */
export function timeToMinutes(time) {
  if (typeof time !== 'string') return null;
  const match = time.trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 24 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/** 870 → "14:30:00" */
export function minutesToTime(total) {
  const wrapped = ((Math.round(total) % 1440) + 1440) % 1440;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`;
}

/** "14:00:00" → "2:00 PM"; with { compact: true } → "2 PM" */
export function formatTime(time, { compact = false } = {}) {
  const total = timeToMinutes(time);
  if (total == null) return '';
  const h24 = Math.floor(total / 60) % 24;
  const m = total % 60;
  const suffix = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  if (compact && m === 0) return `${h12} ${suffix}`;
  return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
}

/** Hours between two times of day; a window ending past midnight wraps (22:00 → 02:00 = 4). */
export function durationHours(start, end) {
  const a = timeToMinutes(start);
  const b = timeToMinutes(end);
  if (a == null || b == null) return null;
  let diff = b - a;
  if (diff <= 0) diff += 24 * 60;
  return diff / 60;
}

/** 4 → "4 hrs", 1 → "1 hr", 4.5 → "4h 30m" */
export function formatDuration(hours) {
  if (hours == null || !Number.isFinite(hours)) return '';
  const whole = Math.floor(hours);
  const minutes = Math.round((hours - whole) * 60);
  if (minutes === 0) return `${whole} ${whole === 1 ? 'hr' : 'hrs'}`;
  if (whole === 0) return `${minutes}m`;
  return `${whole}h ${minutes}m`;
}

export function formatDistance(miles) {
  const n = toNumber(miles);
  if (n < 0.1) return 'Nearby';
  return `${n < 10 ? n.toFixed(1) : Math.round(n)} mi`;
}

export function formatDate(value, format = 'ddd, MMM D') {
  if (!value) return '';
  const d = dayjs(value);
  return d.isValid() ? d.format(format) : '';
}

export function formatCount(count, singular, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

export function initials(first = '', last = '') {
  return `${(first || '').trim().charAt(0)}${(last || '').trim().charAt(0)}`.toUpperCase() || '?';
}
